/**
 * Create Report from Template
 *
 * Creates a Report instance from a ReportTemplate with flat data.values structure.
 */

import { VariableResolver } from '../variableResolver';
import { newId } from '../../utils/ids';
import { computeHighlights } from './computeHighlights';
import { applyVisitPrefillFromPrevious } from './visitValueMapping';
import type { Company, Site, ReportTemplate, Report, DataContext } from '../schemas';
import type { ExpandableCatalog } from '../models/catalog';
import {
  buildBaseTemplateContext,
  mergeTemplateContext,
  type TemplateContextOverrides,
} from './templateContext';
import { DEFAULT_SETTINGS } from '../repos/settingsRepo';
import { formatSiteSequence, formatSiteShorthand } from '@/utils/entityIdentity';
import { formatIsoDate } from '@/utils/date';
import { syncGuPublicationAction } from '../actions/guPublicationActions';
import { evaluateSectionVisibility } from './evaluateSectionVisibility';
import { resolveUnitsPlanningSnapshot } from '@/utils/inspectionPlanning';
import { resolveAddress } from '@/utils/address';
import {
  DEFAULT_INSPECTION_TIME,
  combineDateAndTime,
  resolvePlannedVisitAt,
  toIsoDateTime,
} from '@/utils/inspectionScheduling';
import { normalizeReportScheduling } from './reportScheduling';

interface CreateReportFromTemplateOptions {
  template: ReportTemplate;
  company: Company;
  site: Site;
  variables?: Record<string, unknown>;
  previousReportId?: string;
  previousReport?: Report | null;
  context?: TemplateContextOverrides;
  catalogs?: Record<string, ExpandableCatalog>;
}

/**
 * Create a new Report from a template
 *
 * This function:
 * 1. Builds a DataContext from company/site/variables
 * 2. Uses VariableResolver to resolve template → flat data.values
 * 3. Returns a complete Report ready to persist
 *
 * @example
 * const report = createReportFromTemplate({
 *   template: await TemplatesRepo.getDefault(),
 *   company: await CompaniesRepo.get(companyId),
 *   site: await SitesRepo.getSite(siteId),
 *   variables: { today: '2025-09-29' }
 * });
 *
 * await ReportsRepo.upsert(report);
 */
export function createReportFromTemplate({
  template,
  company,
  site,
  variables,
  previousReportId,
  previousReport,
  context: overrides,
  catalogs,
}: CreateReportFromTemplateOptions): Report {
  const reportId = newId();
  const today = formatIsoDate(new Date());
  const baseVariables = {
    today,
    inspectorName: 'Ispettore',
    nostroRecapito: DEFAULT_SETTINGS.nostroRecapito,
    ...(variables ?? {}),
  } satisfies Record<string, unknown>;

  const baseContext = buildBaseTemplateContext({
    company,
    site,
    variables: baseVariables,
    reportId,
    previousReport: previousReport
      ? { id: previousReport.id, visitDate: previousReport.meta.visitDate }
      : null,
    catalogs,
  });

  const mergedContext: DataContext = mergeTemplateContext(baseContext, overrides);

  const resolver = new VariableResolver(mergedContext);
  let report = resolver.resolveTemplate(template, { reportId });

  if (previousReport) {
    report = applyVisitPrefillFromPrevious(report, template, previousReport);
  }

  report.meta.applicableSections = evaluateSectionVisibility({
    template,
    context: mergedContext,
  });

  // Link to previous report if provided
  if (previousReportId) {
    report.previousReportId = previousReportId;
  } else if (previousReport) {
    report.previousReportId = previousReport.id;
  }

  const plannedVisitAt = resolvePlannedVisitAt(site);
  report.plannedVisitAt = plannedVisitAt ?? undefined;
  if (!report.reportAt) {
    const baseDate = combineDateAndTime(new Date(), DEFAULT_INSPECTION_TIME);
    report.reportAt = plannedVisitAt ?? toIsoDateTime(baseDate);
  }
  report = normalizeReportScheduling(report);

  const visitDate = report.meta.visitDate || today;
  const shorthand = formatSiteShorthand(company, site);
  const sequence = formatSiteSequence(site.sequenceNumber);
  const identifier = shorthand ?? sequence ?? site.name ?? company.code ?? company.name;
  report.meta.title = `Report ${identifier} ${visitDate}`;
  if (!report.meta.company) {
    report.meta.company = company.name;
  }
  report.meta.siteName = site.name;
  if (!report.meta.siteLocation) {
    const resolvedSiteAddress = resolveAddress(
      {
        address: site.sedeOperativaAddress,
        cap: site.sedeOperativaCap,
        city: site.sedeOperativaCity,
        province: site.sedeOperativaProvince,
      },
      site.sedeOperativa ?? site.address
    );
    report.meta.siteLocation = site.siteLocation ?? resolvedSiteAddress;
  }
  const planningSnapshot = resolveUnitsPlanningSnapshot(site.refrigerationUnits ?? []);
  if (planningSnapshot.plannedPeriod || planningSnapshot.plannedDate) {
    report.meta.plannedPeriodSnapshot = planningSnapshot.plannedPeriod;
    report.meta.plannedDateSnapshot = planningSnapshot.plannedDate;
  }

  // Sync GU validation system actions (async but fire-and-forget for now)
  // Note: In a future version, we might want to await this and handle errors
  syncGuPublicationAction({ companyId: company.id, site }).catch((error) => {
    console.warn('[createReportFromTemplate] Failed to create GU validation actions:', error);
  });

  return report;
}

/**
 * Create a new report with change tracking from a previous report
 *
 * This function:
 * 1. Creates a new report from template
 * 2. Links it to the previous report
 * 3. Computes highlights by comparing with previous report
 *
 * @example
 * const latestFinalized = await ReportsRepo.getLatestFinalizedBySite(siteId);
 * const newReport = createReportWithTracking({
 *   template: await TemplatesRepo.getDefault(),
 *   company,
 *   site,
 *   previousReport: latestFinalized
 * });
 */
export function createReportWithTracking({
  template,
  company,
  site,
  variables,
  previousReport,
  onlyModifications = false,
  context,
}: CreateReportFromTemplateOptions & {
  previousReport?: Report | null;
  onlyModifications?: boolean;
}): Report {
  const report = createReportFromTemplate({
    template,
    company,
    site,
    variables,
    previousReportId: previousReport?.id,
    context,
  });

  // Compute highlights if we have a previous report
  if (previousReport) {
    const { highlights } = computeHighlights({
      currentReport: report,
      previousReport,
      onlyModifications,
    });
    report.data.highlights = highlights;
  }

  return report;
}
